import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, type DiscoveredIface } from "../api";

// SpeedUnit packs together a magnitude × multiplier UI without storing both
// independently; we always serialise to bits/sec at the boundary.
type SpeedUnit = "Mbps" | "Gbps";

export interface InterfaceFormValues {
  if_index: number;
  name: string;
  color: string;
  direction: "auto" | "in" | "out";
  stats_mode: "basic" | "extended";
  /** Inbound link speed in bits/sec (0 = unknown). */
  speed_in: number;
  /** Outbound link speed in bits/sec (0 = unknown). */
  speed_out: number;
}

interface FormShape {
  if_index: number;
  name: string;
  color: string;
  direction: "auto" | "in" | "out";
  stats_mode: "basic" | "extended";
  speed_in_value: number;
  speed_in_unit: SpeedUnit;
  speed_out_value: number;
  speed_out_unit: SpeedUnit;
}

interface Props {
  open: boolean;
  /** When set, the modal is in edit mode and pre-fills these values. */
  initial?: InterfaceFormValues;
  /** Sensor ID, used to fire the SNMP discovery walk for the autofill picker. */
  sensorId?: number;
  onCancel: () => void;
  onSave: (v: InterfaceFormValues) => void;
}

// Format a bps value for the picker's Speed column. Mirrors Wansight's
// "10 Gbps" / "100 Mbps" / "0" formatting so a side-by-side comparison
// reads naturally.
function fmtSpeed(bps: number): string {
  if (!bps || bps <= 0) return "0";
  if (bps >= 1_000_000_000 && bps % 1_000_000_000 === 0) return `${bps / 1_000_000_000} Gbps`;
  if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(1)} Gbps`;
  if (bps >= 1_000_000) return `${Math.round(bps / 1_000_000)} Mbps`;
  return `${Math.round(bps / 1_000)} kbps`;
}

// bpsToValueUnit converts a stored bits/sec rate to a UI-friendly
// (number, unit) pair. We pick Gbps for ≥ 1 Gbps so the user sees "10 Gbps"
// instead of "10000 Mbps", matching Wansight's defaults.
function bpsToValueUnit(bps: number): { value: number; unit: SpeedUnit } {
  if (!bps || bps <= 0) return { value: 0, unit: "Mbps" };
  if (bps >= 1_000_000_000 && bps % 1_000_000_000 === 0) {
    return { value: bps / 1_000_000_000, unit: "Gbps" };
  }
  if (bps >= 1_000_000_000) {
    return { value: bps / 1_000_000_000, unit: "Gbps" };
  }
  return { value: bps / 1_000_000, unit: "Mbps" };
}

function valueUnitToBps(value: number, unit: SpeedUnit): number {
  if (!value || value <= 0) return 0;
  return unit === "Gbps" ? value * 1_000_000_000 : value * 1_000_000;
}

export default function InterfaceModal({
  open,
  initial,
  sensorId,
  onCancel,
  onSave,
}: Props) {
  const [form] = Form.useForm<FormShape>();
  const [picker, setPicker] = useState<DiscoveredIface[]>([]);
  const [pickerErr, setPickerErr] = useState<string>("");
  const [pickerLoading, setPickerLoading] = useState(false);

  // Fire the SNMP walk once when the modal opens. Empty list + non-empty
  // error = SNMP not configured / unreachable; the operator can still type
  // values manually so the picker is purely additive.
  async function refreshPicker() {
    if (!sensorId) return;
    setPickerLoading(true);
    setPickerErr("");
    try {
      const res = await api.discoverInterfaces(sensorId);
      setPicker(res.interfaces || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPickerErr(msg);
      setPicker([]);
    } finally {
      setPickerLoading(false);
    }
  }

  useEffect(() => {
    if (open && sensorId) refreshPicker();
    if (!open) {
      setPicker([]);
      setPickerErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sensorId]);

  // Click handler for picker rows: copy the SNMP row's fields into the form,
  // preserving anything the operator already typed (color, direction, stats).
  function autofill(row: DiscoveredIface) {
    const speedUnit: SpeedUnit =
      row.speed_bps >= 1_000_000_000 ? "Gbps" : "Mbps";
    const speedValue =
      speedUnit === "Gbps"
        ? row.speed_bps / 1_000_000_000
        : Math.round(row.speed_bps / 1_000_000);
    form.setFieldsValue({
      if_index: row.if_index,
      name: row.description || row.name,
      speed_in_value: speedValue,
      speed_in_unit: speedUnit,
      speed_out_value: speedValue,
      speed_out_unit: speedUnit,
    });
    message.success(`Filled from SNMP idx ${row.if_index} (${row.name})`);
  }

  const pickerColumns: ColumnsType<DiscoveredIface> = [
    { title: "Index", dataIndex: "if_index", width: 60 },
    {
      title: "Interface",
      dataIndex: "name",
      ellipsis: true,
      render: (v: string) => <span style={{ fontFamily: "monospace" }}>{v}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: "Speed",
      dataIndex: "speed_bps",
      width: 90,
      render: (v: number) => fmtSpeed(v),
    },
    {
      title: "Status",
      dataIndex: "oper_status",
      width: 70,
      render: (v: string) => (
        <Tag color={v === "up" ? "green" : "default"}>{v}</Tag>
      ),
    },
  ];

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const sin = bpsToValueUnit(initial.speed_in);
      const sout = bpsToValueUnit(initial.speed_out);
      form.setFieldsValue({
        if_index: initial.if_index,
        name: initial.name,
        direction: initial.direction,
        stats_mode: initial.stats_mode,
        speed_in_value: sin.value,
        speed_in_unit: sin.unit,
        speed_out_value: sout.value,
        speed_out_unit: sout.unit,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        if_index: 0,
        name: "",
        direction: "auto",
        stats_mode: "extended",
        speed_in_value: 0,
        speed_in_unit: "Gbps",
        speed_out_value: 0,
        speed_out_unit: "Gbps",
      });
    }
  }, [open, initial, form]);

  async function handleOk() {
    try {
      const v = await form.validateFields();
      onSave({
        if_index: Number(v.if_index),
        name: v.name?.trim() ?? "",
        // Interface colour is legacy (chart colour now lives on the sensor).
        // Preserve any existing value; new interfaces carry none.
        color: initial?.color ?? "",
        direction: v.direction,
        stats_mode: v.stats_mode,
        speed_in: valueUnitToBps(Number(v.speed_in_value || 0), v.speed_in_unit),
        speed_out: valueUnitToBps(Number(v.speed_out_value || 0), v.speed_out_unit),
      });
    } catch {
      // antd already surfaces field errors
    }
  }

  // Single column with a 100-px label column (Wansight uses ~92 px); the
  // speed rows are split into [number] [unit] using nested Cols.
  return (
    <Modal
      open={open}
      title={initial ? "Edit Interface" : "Add Interface"}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Save"
      width={760}
      destroyOnClose
    >
      <Form<FormShape>
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="right"
        size="small"
        colon
      >
        <Form.Item
          name="if_index"
          label="SNMP Index"
          rules={[{ required: true, message: "SNMP index is required" }]}
        >
          <InputNumber min={0} max={4294967295} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Interface Name"
          tooltip="Free-form label shown in dashboards. Common pattern is the router's interface short name (e.g. UL_To_CMC_IXP_Vlan243)."
        >
          <Input placeholder="e.g. UL_To_CMC_IXP_Vlan243_Main" />
        </Form.Item>
        <Form.Item
          name="direction"
          label="Traffic Direction"
          tooltip="Auto: count flows that traverse this interface in either direction. In/Out: only count flows whose ingress/egress matches — useful when an exporter sees the same flow on multiple interfaces and you want to attribute it to one."
        >
          <Select
            options={[
              { value: "auto", label: "Auto" },
              { value: "in", label: "In (downstream)" },
              { value: "out", label: "Out (upstream)" },
            ]}
          />
        </Form.Item>
        {/* Stats Engine intentionally NOT exposed here. Wansight puts the
            knob at the per-interface level, but sflow's aggregator is
            per-sensor (one stats mode covers every interface on a given
            sensor) — see "Stats Engine" on the Flow Sensor Configuration
            modal. The DB column stats_mode is preserved for forward-compat
            until the per-interface refactor (plan §P1.2 follow-up). */}
        <Form.Item label="Link Speed IN" required={false}>
          <Row gutter={4}>
            <Col span={14}>
              <Form.Item name="speed_in_value" noStyle>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="0 = unknown" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="speed_in_unit" noStyle>
                <Select
                  options={[
                    { value: "Mbps", label: "Mbps" },
                    { value: "Gbps", label: "Gbps" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item label="Link Speed OUT">
          <Row gutter={4}>
            <Col span={14}>
              <Form.Item name="speed_out_value" noStyle>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="0 = unknown" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="speed_out_unit" noStyle>
                <Select
                  options={[
                    { value: "Mbps", label: "Mbps" },
                    { value: "Gbps", label: "Gbps" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
      </Form>

      {/* SNMP "Click to Autofill" picker. Same UX as Wansight: a scrollable
          table of IF-MIB rows; clicking a row populates the form above. */}
      {sensorId && (
        <fieldset
          style={{
            marginTop: 8,
            border: "1px solid var(--line)",
            padding: 0,
          }}
        >
          <legend
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "0 6px",
              marginLeft: 8,
              color: "var(--strong)",
            }}
          >
            Click to Autofill
          </legend>
          <div
            style={{
              padding: 6,
              background: "var(--bg-2)",
              borderBottom: "1px solid var(--line)",
              display: "flex",
              gap: 6,
              alignItems: "center",
            }}
          >
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={pickerLoading}
              onClick={refreshPicker}
            >
              Refresh
            </Button>
            <span style={{ fontSize: 11, color: "var(--muted)" }}>
              SNMP walk of IF-MIB on the sensor's exporter. Configure
              snmp_community on the sensor first.
            </span>
          </div>
          {pickerErr && (
            <Alert
              type="warning"
              showIcon
              message="SNMP discovery failed"
              description={pickerErr}
              style={{ margin: 6 }}
            />
          )}
          <Table
            rowKey="if_index"
            size="small"
            dataSource={picker}
            columns={pickerColumns}
            pagination={false}
            loading={pickerLoading}
            scroll={{ y: 220 }}
            onRow={(row) => ({
              onClick: () => autofill(row),
              style: { cursor: "pointer" },
            })}
            locale={{
              emptyText: pickerErr
                ? "Discovery failed — type values manually."
                : "No interfaces yet — click Refresh to walk IF-MIB.",
            }}
          />
        </fieldset>
      )}
    </Modal>
  );
}
